import React, { useState, useEffect, useMemo } from "react";
import axios from "./axiosInstance";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faSpinner } from "@fortawesome/free-solid-svg-icons";

const getRecords = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
}
const setRecords = (key, val) => localStorage.setItem(key, JSON.stringify(val))
const upsertById = (arr, rec) => {
  const i = arr.findIndex(r => r.id === rec.id)
  if (i === -1) arr.push(rec); else arr[i] = rec
  return arr
}
const formatDateTime = (iso) => {
  if (!iso) return "—"
  const m = moment(iso)
  return moment().diff(m, "hours") < 24 ? m.fromNow() : m.format("DD/MM/YYYY HH:mm")
}

const toBackendPayload = (r) => {
  const {
    name, dob, gender, age, height, weight, heightUnit, weightUnit,
    bmi, bmiCategory, waistCircumferenceCm, waistCategory, bloodPressure, pulse, blood, glucose,
    additionalResults, cholesterol, weeklyUnitIntake, weeklyHours, smokerStatus,
    services, additionalNotes, email, optOut
  } = r
  return {
    name, dob, gender, age, height, weight, heightUnit, weightUnit,
    bmi, bmiCategory, waistCircumferenceCm, waistCategory, bloodPressure, pulse, blood, glucose,
    additionalResults, cholesterol, weeklyUnitIntake, weeklyHours, smokerStatus,
    services, additionalNotes, email, optOut
  }
}

const PER_PAGE = 10;

const StatusBadge = ({ status }) => {
  const s = (status || "pending").toLowerCase();
  const cls =
    s === "submitted" ? "bg-green-100 text-green-800" :
    s === "error"     ? "bg-red-100 text-red-800" :
                        "bg-yellow-100 text-yellow-800";
  const label = s === "error" ? "Failed" : s.charAt(0).toUpperCase() + s.slice(1);
  return <span className={`ml-1 inline-block px-2 py-0.5 rounded text-xs ${cls}`}>{label}</span>
}

const Pager = ({ page, totalPages, onChange }) => (
  <div className="flex items-center gap-2 text-sm">
    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1}>Prev</button>
    <span>Page {page} / {totalPages}</span>
    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>Next</button>
  </div>
)

const Admin = () => {
  const [unsentForms, setUnsentForms] = useState([])
  const [archivedForms, setArchivedForms] = useState([])
  const [searchUnsent, setSearchUnsent] = useState("")
  const [searchArchived, setSearchArchived] = useState("")
  const [pageUnsent, setPageUnsent] = useState(1)
  const [pageArchived, setPageArchived] = useState(1)
  const [savingAll, setSavingAll] = useState(false)
  const [submittingId, setSubmittingId] = useState(null)
  const navigate = useNavigate()
  const [sortArchived, setSortArchived] = useState("created_desc")

  const loadData = () => {
    setUnsentForms(getRecords("healthScreenData"))
    setArchivedForms(getRecords("allHealthScreenData"))
  }

  useEffect(() => {
    loadData()
    const onStorage = () => loadData()
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  // ---------- filtering + pagination ----------
  const filteredUnsent = useMemo(() => {
    const query = searchUnsent.trim().toLowerCase()
    if (!query) return unsentForms
    return unsentForms.filter(form =>
      [form.name, form.email, form.bmiCategory, form.smokerStatus].filter(Boolean)
        .some(value => String(value).toLowerCase().includes(query))
    );
  }, [unsentForms, searchUnsent])

  const totalPagesUnsent = Math.max(1, Math.ceil(filteredUnsent.length / PER_PAGE));
  const pagedUnsent = useMemo(() => {
    const start = (pageUnsent - 1) * PER_PAGE;
    const sorted = [...filteredUnsent].sort(
      (newerForm, olderForm) =>
        moment(olderForm.createdAt).valueOf() - moment(newerForm.createdAt).valueOf()
    )
  
    return sorted.slice(start, start + PER_PAGE)
  }, [filteredUnsent, pageUnsent])

  useEffect(() => { if (pageUnsent > totalPagesUnsent) setPageUnsent(1); }, [totalPagesUnsent, pageUnsent]);

  const filteredArchived = useMemo(() => {
    const query = searchArchived.trim().toLowerCase()
    if (!query) return archivedForms
    return archivedForms.filter(form =>
      [form.name, form.email, form.status, form.bmiCategory, form.smokerStatus].filter(Boolean)
        .some(value => String(value).toLowerCase().includes(query))
    )
  }, [archivedForms, searchArchived])

  const sortedArchived = useMemo(() => {
    const formsCopy = [...filteredArchived];
  
    switch (sortArchived) {
      case "name_asc":
        return formsCopy.sort((firstForm, secondForm) =>
          (firstForm.name || "").localeCompare(secondForm.name || "", undefined, { sensitivity: "base" })
        );
  
      case "name_desc":
        return formsCopy.sort((firstForm, secondForm) =>
          (secondForm.name || "").localeCompare(firstForm.name || "", undefined, { sensitivity: "base" })
        );
  
      case "created_asc": // oldest → newest
        return formsCopy.sort(
          (earlierForm, laterForm) =>
            moment(earlierForm.createdAt).valueOf() - moment(laterForm.createdAt).valueOf()
        );
  
      case "created_desc": // newest → oldest
        return formsCopy.sort(
          (earlierForm, laterForm) =>
            moment(laterForm.createdAt).valueOf() - moment(earlierForm.createdAt).valueOf()
        );
  
      case "age_asc": // youngest → oldest
        return formsCopy.sort(
          (youngerForm, olderForm) => (Number(youngerForm.age) || 0) - (Number(olderForm.age) || 0)
        );
  
      case "age_desc": // oldest → youngest
        return formsCopy.sort(
          (youngerForm, olderForm) => (Number(olderForm.age) || 0) - (Number(youngerForm.age) || 0)
        );
  
      default:
        return formsCopy;
    }
  }, [filteredArchived, sortArchived])

  const totalPagesArchived = Math.max(1, Math.ceil(sortedArchived.length / PER_PAGE))
  const pagedArchived = useMemo(() => {
    const start = (pageArchived - 1) * PER_PAGE;
    return sortedArchived.slice(start, start + PER_PAGE);
  }, [sortedArchived, pageArchived])

  useEffect(() => {
    if (pageArchived > totalPagesArchived) setPageArchived(1)
  }, [totalPagesArchived, pageArchived])

  const updateArchiveStatus = (rec, status, extra = {}) => {
    let archive = getRecords("allHealthScreenData")
    const updated = { ...rec, status, updatedAt: new Date().toISOString(), ...extra }
    archive = upsertById(archive, updated)
    setRecords("allHealthScreenData", archive)
  }

  const removeFromQueue = (id) => {
    const newQueue = getRecords("healthScreenData").filter(r => r.id !== id)
    setRecords("healthScreenData", newQueue)
  }

  const refresh = () => {
    setUnsentForms(getRecords("healthScreenData"))
    setArchivedForms(getRecords("allHealthScreenData"))
  }

  const submitRecord = async (record) => {
    if (!navigator.onLine) {
      Swal.fire("Offline", "No internet connection. Try again later.", "warning")
      return
    }
    if (submittingId) return
    setSubmittingId(record.id)
    try {
      const payload = toBackendPayload(record)
      const response = await axios.post("health-screen-report/create", payload)

      if (response.status === 200) {
        removeFromQueue(record.id)
        // Archive: DO NOT remove — just mark submitted
        updateArchiveStatus(record, "submitted", {
          submittedAt: new Date().toISOString(),
          backendId: response?.data?.id ?? record.backendId ?? null,
          errorMessage: null,
        });
        Swal.fire("Submitted", `${record.name}'s form was submitted.`, "success")
      } else {
        throw new Error("Unexpected response status")
      }
    } catch (err) {
        let combinedMessage = err?.message || "Unknown error"

        if (err.response?.data?.message) {
          combinedMessage += ` ${err.response.data.message}`
        }

        // Update queue (healthScreenData)
        let queue = getRecords("healthScreenData")
        queue = queue.map((rec) =>
          rec.id === record.id
            ? {
                ...rec,
                status: "error",
                errorMessage: combinedMessage,
                updatedAt: moment().toISOString(),
              }
            : rec
        )
        setRecords("healthScreenData", queue)

        // Archive: DO NOT remove — just mark failed
        updateArchiveStatus(record, "error", { errorMessage: combinedMessage })

        Swal.fire("Error", combinedMessage, "error")
    } finally {
      setSubmittingId(null)
      refresh()
    }
  }

  const handleSaveAll = async () => {
    if (!navigator.onLine) return Swal.fire("Offline", "No internet connection. Try again later.", "warning")
    const queueSnapshot = getRecords("healthScreenData");
    if (queueSnapshot.length === 0) return Swal.fire("Nothing to submit", "No unsent forms to save.", "info")

    setSavingAll(true)
    let success = 0, failure = 0

    for (const record of queueSnapshot) {
      try {
        const payload = toBackendPayload(record);
        const response = await axios.post("health-screen-report/create", payload)

        if (response.status === 200) {
          removeFromQueue(record.id)
          updateArchiveStatus(record, "submitted", {
            submittedAt: new Date().toISOString(),
            backendId: response?.data?.id ?? record.backendId ?? null,
            errorMessage: null,
          })
          success++
        } else {
          throw new Error("Unexpected response status")
        }
      } catch (err) {
        let query = getRecords("healthScreenData")
        query = query.map(rec => rec.id === record.id ? { ...rec, status: "error", errorMessage: err?.message, updatedAt: new Date().toISOString() } : rec)
        setRecords("healthScreenData", query)
        updateArchiveStatus(record, "error", { errorMessage: err?.message })
        failure++
      }
    }

    setSavingAll(false)
    refresh()

    if (failure === 0) Swal.fire("All forms sent!", `${success} forms submitted successfully.`, "success")
    else Swal.fire("Some forms failed", `${success} submitted, ${failure} failed.`, "warning")
  }

  return (
    <div className="relative container mx-auto p-4">
      <div className="text-2xl font-bold text-sas-primary">Admin Portal</div>

      <button
        className="absolute top-0 right-0 rounded-lg px-4 py-2 bg-sas-primary text-white hover:bg-blue-700 mt-4 mr-4"
        onClick={() => navigate("/")}
      >
        Back to Health Screening
      </button>

      {/* Unsent */}
      <div className="mx-auto max-w-5xl mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-sas-primary">Unsent Health Screenings</h2>
          <div className="flex items-center gap-2">
           <div>
            <label for="default-search" class="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">Search</label>
            <div className="relative w-full">
                <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                    </svg>
                </div>
                <input value={searchUnsent}
                onChange={(e) => { setSearchUnsent(e.target.value); setPageUnsent(1); }}
                type="search" 
                id="default-search" 
                className="block px-4 py-2.5 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Search name, email, status..." />
            </div>
           </div>
            <button
              onClick={handleSaveAll}
              className="bg-sas-primary text-white py-2 px-4 rounded-md font-semibold hover:bg-sas-primary-hover transition disabled:opacity-60"
              disabled={savingAll || filteredUnsent.length === 0 || submittingId}
              title={submittingId ? "Wait for current submission" : "Submit all pending"}
            >
              {savingAll ? "Submitting..." : "Save All"}
            </button>
          </div>
        </div>

        {pagedUnsent.length === 0 ? (
          <p className="text-gray-600">No unsent forms found.</p>
        ) : (
          <>
            <ul className="border p-4 rounded-md bg-white shadow-md divide-y">
              {pagedUnsent.map((form) => (
                <li key={form.id ?? form.createdAt} className="py-3">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="font-semibold text-sas-primary">
                        {form.name} {form.age ? <span className="text-gray-600 font-normal"> · {form.age}</span> : null} 
                      </div>
                      <div className="text-sm text-gray-700">
                        <span className="mr-4"><strong>Email:</strong> {form.email || "—"}</span>
                        <span className="mr-4"><strong>Status:</strong> <StatusBadge status={form.status} /></span>
                        <span><strong>Created:</strong> {formatDateTime(form.createdAt)}</span>
                      </div>
                    </div>
                    <button
                      className="inline-flex items-center gap-2 bg-sas-primary text-white px-3 py-2 rounded-md font-semibold hover:bg-sas-primary-hover transition disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={() => !submittingId && submitRecord(form)}
                      disabled={Boolean(submittingId)}
                      title={submittingId ? "Wait for current submission" : "Submit this form"}
                    >
                      {submittingId === form.id ? <><FontAwesomeIcon icon={faSpinner} spin /> Submitting...</> : <><FontAwesomeIcon icon={faPaperPlane} /> Submit</>}
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-3 flex justify-end">
              <Pager page={pageUnsent} totalPages={totalPagesUnsent} onChange={setPageUnsent} />
            </div>
          </>
        )}
      </div>

      {/* Archived (all statuses, never cleared) */}
      <div className="mx-auto max-w-5xl mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-sas-primary">Archived Health Screenings</h2>
          <div className="flex items-center gap-2">
            <div>
              <label for="default-search" class="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">Search</label>
              <div className="relative w-full">
                  <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                      <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                      </svg>
                  </div>
                  <input value={searchArchived}
                  onChange={(e) => { setSearchArchived(e.target.value); setPageArchived(1); }}
                  type="search" 
                  id="default-search" 
                  className="block p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Search name, email, status..." />
              </div>
            </div>
            <select
              className="block w-full p-4 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              value={sortArchived}
              onChange={(e) => { setSortArchived(e.target.value); setPageArchived(1); }}
              aria-label="Sort archived"
            >
              <option value="created_desc">Date: Newest → Oldest</option>
              <option value="created_asc">Date: Oldest → Newest</option>
              <option value="name_asc">Name: A → Z</option>
              <option value="name_desc">Name: Z → A</option>
              <option value="age_asc">Age: Youngest → Oldest</option>
              <option value="age_desc">Age: Oldest → Youngest</option>
            </select>
          </div>
        </div>

        {pagedArchived.length === 0 ? (
          <p className="text-gray-600">No archived forms found.</p>
        ) : (
          <>
            <ul className="border p-4 rounded-md bg-white shadow-md divide-y">
              {pagedArchived.map((form) => {
                const canResend = (form.status !== "submitted");
                return (
                  <li key={form.id ?? form.createdAt} className="py-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <div className="font-semibold text-sas-primary">
                          {form.name} {form.age ? <span className="text-gray-600 font-normal">· {form.age}</span> : null} {form.gender ? <span className="text-gray-600 font-normal"> · {form.gender}</span> : null}
                        </div>
                        <div className="text-sm text-gray-700">
                          <span className="mr-4"><strong>Email:</strong> {form.email || "—"}</span>
                          <span className="mr-4"><strong>Status:</strong> <StatusBadge status={form.status} /></span>
                          <span className="mr-4"><strong>Created:</strong> {formatDateTime(form.createdAt)}</span>
                          <span><strong>Submitted:</strong> {formatDateTime(form.submittedAt)}</span>
                        </div>
                        {form.errorMessage && form.status === "error" && (
                          <div className="mt-2 text-sm text-red-700">
                            <strong>Error:</strong> {form.errorMessage}
                          </div>
                        )}
                      </div>

                      <button
                        className="inline-flex items-center gap-2 bg-sas-primary text-white px-3 py-2 rounded-md font-semibold hover:bg-sas-primary-hover transition disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={() => canResend && !submittingId && submitRecord(form)}
                        disabled={Boolean(submittingId)}
                        title={
                          !canResend ? "Already submitted" :
                          submittingId ? "Wait for current submission" :
                          "Resubmit this form"
                        }
                      >
                        {submittingId === form.id ? (
                          <><FontAwesomeIcon icon={faSpinner} spin /> Submitting...</>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            Submit
                          </>
                        )}
                      </button>
                    
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-3 flex justify-end">
              <Pager page={pageArchived} totalPages={totalPagesArchived} onChange={setPageArchived} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Admin;


